import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  fetchRetailPricing,
  saveRetailPricing,
  deleteRetailPricing,
} from '@/api/retailPricing'
import type { SaveRetailPricingRequest } from '@/api/retailPricing'
import { queryKeys } from '@/lib/queryKeys'
import { toast } from '@/stores/useToastStore'
import { AxiosError } from 'axios'

function extractErrorMessage(error: unknown): string {
  if (error instanceof AxiosError && error.response?.data?.detail) {
    const detail = error.response.data.detail
    if (typeof detail === 'string') return detail
    if (Array.isArray(detail) && detail.length > 0) {
      return detail[0]?.msg ?? 'An error occurred'
    }
  }
  return 'An error occurred'
}

export function useRetailPricing(
  planId: string | undefined,
  versionId: string | undefined,
  lineId: string | undefined
) {
  return useQuery({
    queryKey: queryKeys.retailPricing.byLine(
      planId ?? '',
      versionId ?? '',
      lineId ?? ''
    ),
    queryFn: () => fetchRetailPricing(planId!, versionId!, lineId!),
    enabled: !!planId && !!versionId && !!lineId,
  })
}

export function useSaveRetailPricing(planId: string, versionId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      lineId,
      body,
    }: {
      lineId: string
      body: SaveRetailPricingRequest
    }) => saveRetailPricing(planId, versionId, lineId, body),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.retailPricing.byLine(
          planId,
          versionId,
          variables.lineId
        ),
      })
      toast.success('Pricing model saved successfully')
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error))
    },
  })
}

export function useDeleteRetailPricing(planId: string, versionId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ lineId }: { lineId: string }) =>
      deleteRetailPricing(planId, versionId, lineId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.retailPricing.byLine(
          planId,
          versionId,
          variables.lineId
        ),
      })
      toast.success('Pricing model removed successfully')
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error))
    },
  })
}
